import { DynamicTableColumn, Organization, SecurityTask, TableAction, toTaskDescription } from '@newgenus/common';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgBoardService } from '../../services/org-board-service';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from '../../../material.module';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { NgClass } from '@angular/common';

@Component({
    selector: 'shared-org-notification-list-dialog',
    imports: [MaterialModule, NgClass, MatMenuModule, MatButtonModule, MatIconModule],
    templateUrl: './org-notification-list-dialog.html',
    styleUrls: ['./org-notification-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgNotificationListDialogComponent implements OnInit, OnDestroy {
    public columns: DynamicTableColumn<SecurityTask>[] = [
        {
            columnDef: 'desc',
            header: 'Task',
            cell: (task: SecurityTask) =>
                toTaskDescription(task, (uid) => (this.data?.organization as any)?.orgUsers?.[uid]?.name),
        },
        {
            columnDef: 'status',
            header: 'Status',
            cell: (task: SecurityTask) =>
                task.status.charAt(0).toUpperCase() + task.status.slice(1) + taskStatusIcon(task),
        },
        {
            columnDef: 'completedOn',
            header: 'Completed On',
            cell: (task: SecurityTask) => {
                const date = task.completedTimestamp;
                return date && date.toDate ? date.toDate().toDateString() : '';
            },
        },
        {
            columnDef: 'createdOn',
            header: 'Created On',
            cell: (task: SecurityTask) => {
                const date = task.createdTimeStamp;
                return date && date.toDate ? date.toDate().toDateString() : '';
            },
        },
        {
            columnDef: 'actions',
            header: '',
            cell: (row) => '',
        },
    ];
    public columnActions: TableAction<SecurityTask>[] = [
        {
            display: 'View',
            action: (row) => this.openNotification(row),
            color: 'primary',
            disabled: false,
        },
    ];

    public dataSource = new MatTableDataSource<SecurityTask>([]);
    public displayedColumns = this.columns.map((c) => c.columnDef);
    private destroy$ = new Subject<void>();

    public snackbar = inject(MatSnackBar);
    private dialogService = inject(OrgDialogService);
    private obService = inject(OrgBoardService);

    constructor(
        public dialogRef: MatDialogRef<OrgNotificationListDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
        }
    ) {}

    public ngOnInit(): void {
        this.obService
            .monitorSecurityTasks()
            .pipe(takeUntil(this.destroy$))
            .subscribe((tasks) => {
                this.dataSource.data = tasks;
            });

        this.dataSource.filterPredicate = (data: SecurityTask, filter: string) => {
            const str = JSON.stringify(data).toLowerCase();
            return str.indexOf(filter) !== -1;
        };
    }

    public openNotification(row: SecurityTask): void {
        this.dialogService.openOrgNotification(row);
    }

    public get columnsWithoutActions(): DynamicTableColumn[] {
        return this.columns.filter((c) => c.columnDef !== 'actions');
    }

    public applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value || '';
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

function taskStatusIcon(task: SecurityTask) {
    if (task.status === 'completed') {
        return ' ✅';
    } else if (task.status === 'processing') {
        return ' 🔄';
    } else if (task.status === 'queued') {
        return ' ⌛';
    } else if (task.status === 'failed' || task.status === 'error') {
        return ' ❌';
    } else {
        return '';
    }
}
